import React, { ReactNode, useMemo } from 'react';
import { StoreApi } from 'zustand';
import { createWithEqualityFn } from 'zustand/traditional';
import dayjs from 'dayjs';
import { LoginReponse } from '../../model/login_response';
import { AreasReponse } from '../../model/areas_response';
import { PermissionName } from '../../valueobject/permissionName';
import { Role } from '../../valueobject/role';
import { SessionStore, SessionStoreProvider } from './index';
import { AreaCollection } from '../../utils/area_collection';
import { BusinessType } from '../../valueobject/business_type';
import { BusinessTypeKeys } from '../../constant';

/**
 * セッション情報 デフォルトのDIを作成
 */
export const defaultSessionStateCreator: () => StoreApi<SessionStore> = () => {

  /**
   * ログイン情報取得
   */
  const getLoginInfo = () => {
    const loginInfoStr = sessionStorage.getItem('loginInfo');
    if (!window.session.isLogin) {
      sessionStorage.removeItem('loginInfo');
      return undefined;
    }
    if (loginInfoStr) {
      return JSON.parse(loginInfoStr) as LoginReponse;
    }
    return window.session.user as LoginReponse;
  };

  /**
   * エリア情報取得
   */
  const getAreasInfo = () => {
    const areasInfoStr = sessionStorage.getItem('areasInfo');
    if (!window.session.isLogin) {
      sessionStorage.removeItem('areasInfo');
      return undefined;
    }
    if (areasInfoStr) {
      return JSON.parse(areasInfoStr) as AreasReponse;
    }
    return window.session.areas as AreasReponse;
  };

  /**
   * LocalStorage取得
   * @param key
   */
  const getLocalStorage = (key: string) => {
    const info = getLoginInfo();
    if (info === undefined) {
      return undefined;
    }
    const res = localStorage.getItem(`${info.user.id}-${key}`);
    if (res === null || res === '') {
      const areas = getAreasInfo();
      if (areas) {
        const area = new AreaCollection(areas);
        if (key === 'divisionId') {
          return area.divisions()?.getDefault() ?? 0;
        }
        if (key === 'floorGroupId') {
          return area.floorGroups(area.divisions().getDefault())?.getDefault() ?? 0;
        }
        return 0;
      }
      return undefined;
    }
    return parseInt(res, 10);
  };

  /**
   * LocalStorage設定
   * @param info
   * @param key
   * @param id
   */
  const setLocalStorage = (info: LoginReponse | undefined, key: string, id: number) => {
    if (info === undefined) {
      return;
    }
    localStorage.setItem(`${info.user.id}-${key}`, id.toString());
  };

  return createWithEqualityFn<SessionStore>((set, get) => {
    return {
      loginInfo: getLoginInfo(),
      areasInfo: getAreasInfo(),
      selectedDivisionId: getLocalStorage('divisionId'),
      selectedFloorGroupId: getLocalStorage('floorGroupId'),
      selectedFloorId: getLocalStorage('floorId'),

      setSelectedDivisionId: (id: number) => {
        const { loginInfo } = get();
        setLocalStorage(loginInfo, 'divisionId', id);
        set({ selectedDivisionId: id });

        const { areasInfo, selectedFloorGroupId, setSelectedFloorGroupId } = get();
        if (areasInfo) {
          const info: AreasReponse = areasInfo;
          const division = info.find((div) => div.id === id);
          if (division === undefined) {
            return;
          }
          if (division.floorGroups.find((fg) => fg.id === selectedFloorGroupId)) {
            return;
          }
          const defaultFloorGroup = division.floorGroups.find((fg) => fg.isDefault);
          if (defaultFloorGroup) {
            setSelectedFloorGroupId(defaultFloorGroup.id);
          } else if (division.floorGroups.length > 0) {
            setSelectedFloorGroupId(division.floorGroups[0].id);
          }
        } else {
          window.console.warn('areasInfo is undefined');
        }
      },

      setSelectedFloorGroupId: (id: number) => {
        // 選択されたフロアグループの中のフロアのデフォルトを取得してデフォルトフロアに指定する
        const { loginInfo } = get();
        setLocalStorage(loginInfo, 'floorGroupId', id);
        set({ selectedFloorGroupId: id });

        const { areasInfo, selectedDivisionId, selectedFloorId, setSelectedFloorId } = get();
        if (areasInfo) {
          const info: AreasReponse = areasInfo;
          const division = info.find((div) => div.id === selectedDivisionId);
          if (division === undefined) {
            return;
          }
          const floorGroup = division.floorGroups.find((fg) => fg.id === id);
          if (floorGroup) {
            if (floorGroup.floors.find((fl) => fl.id === selectedFloorId)) {
              return;
            }

            const defalutFloor = floorGroup.floors.find((fl) => fl.isDefault);
            if (defalutFloor) {
              setSelectedFloorId(defalutFloor.id);
            }
          }
        }
      },

      setSelectedFloorId: (id: number) => {
        // デフォルトフロアに指定する
        const { loginInfo } = get();
        setLocalStorage(loginInfo, 'floorId', id);
        set({ selectedFloorId: id });
      },

      hasSession: (() => {
        const loginInfo = getLoginInfo();
        return loginInfo !== undefined;
      })(),
      logoutTime: undefined,

      login: (login: LoginReponse | undefined) => {
        if (login === undefined) {
          sessionStorage.removeItem('loginInfo');
          sessionStorage.removeItem('areasInfo');
          set({ loginInfo: undefined, areasInfo: undefined, hasSession: false });
          return;
        }
        sessionStorage.setItem('loginInfo', JSON.stringify(login));
        set({ loginInfo: login, hasSession: true });
      },

      logout: () => {
        const { login } = get();
        const { setAreaInSession } = get();
        set({ logoutTime: dayjs() });
        login(undefined);
        setAreaInSession(undefined);
      },

      hasPermission: (permissionName: PermissionName, requireWrite?: boolean, allowBusinessTypes?: BusinessTypeKeys[]) => {
        const { hasSession, loginInfo } = get();
        if (!hasSession) {
          return false;
        }

        const role = new Role(loginInfo?.user?.role);
        if (!role.hasPermission(permissionName, requireWrite)) {
          return false;
        }

        if (allowBusinessTypes) {
          const businessType = loginInfo?.user?.company?.businessType;
          if (businessType === undefined || businessType === BusinessTypeKeys.none || businessType === BusinessTypeKeys.bispot || (businessType !== BusinessTypeKeys.all && !allowBusinessTypes.includes(businessType))) {
            return false;
          }
        }
        return true;
      },

      setAreaInSession: (areas: AreasReponse | undefined) => {
        if (areas === undefined) {
          sessionStorage.removeItem('areasInfo');
          sessionStorage.removeItem('loginInfo');
          sessionStorage.removeItem('areasInfo');
          set({ areasInfo: undefined, selectedDivisionId: undefined, selectedFloorGroupId: undefined, selectedFloorId: undefined });
          return;
        }
        const { selectedDivisionId, setSelectedDivisionId } = get();

        sessionStorage.setItem('areasInfo', JSON.stringify(areas));
        set({ areasInfo: areas });

        if (selectedDivisionId === undefined || selectedDivisionId <= 0) {
          const defaultDivision = areas.find((division) => division.isDefault);
          if (defaultDivision) {
            setSelectedDivisionId(defaultDivision.id);
          } else if (areas.length > 0) {
            setSelectedDivisionId(areas[0].id);
          }
        } else {
          setSelectedDivisionId(selectedDivisionId);
        }

      },
    };
  }, Object.is);
};

/**
 * デフォルトのセッションDIを提供する。
 * @param children 子要素
 * @constructor
 */
export const DefaultSessionProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const store = useMemo(() => defaultSessionStateCreator, []);
  return (
      <SessionStoreProvider storeCreator={store}>
        {children}
      </SessionStoreProvider>
  );
};
